import classnames from "classnames";
import React, { FC, memo, useCallback, useContext, useState } from "react";
import { Link } from "react-router-dom";
import { useMedia } from "react-use";

import { MEDIA_SIZE_MD } from "@/core/media";
import { Flex } from "@/eworkitComponents";
import { useOnLanguageChange } from "@/hooks/Translations";
import { SessionContext } from "@/infrastructure/Session";
import { useLegalDocumentsGuard } from "@/views/components/AgreementsGuard";
import { Container } from "@/views/components/Container";
import { VeramaLogo } from "@/views/components/Logo";
import { EworkLogo } from "@/views/components/Logo/EworkLogo";
import { router } from "@/views/router";

import { Authenticated, NavButtons, Navigation, PublicNavigation } from "./components";

interface ITopbarProps {
  showNavigation?: boolean;
  transparent?: boolean;
  isPublic?: boolean;
}

export const Topbar: FC<ITopbarProps> = memo(({ showNavigation = false, transparent = false, isPublic = true }) => {
  const { authenticated } = useContext(SessionContext);
  const isMediumScreen = useMedia(MEDIA_SIZE_MD);
  const [isMobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const { hasRequiredLegalDocumentsToAccept } = useLegalDocumentsGuard();

  const handleOpen = useCallback(() => {
    setMobileNavigationOpen(true);
  }, [setMobileNavigationOpen]);
  const handleClose = useCallback(() => {
    setMobileNavigationOpen(false);
  }, [setMobileNavigationOpen]);

  const baseClass = "topbar";
  const lang = useOnLanguageChange();

  return (
    <div
      className={classnames(baseClass, {
        [`${baseClass}--with-background`]: !transparent,
        [`${baseClass}--medium-screen`]: isMediumScreen,
        [`${baseClass}--sticky`]: isMediumScreen && isMobileNavigationOpen,
      })}>
      <Container
        className={`${baseClass}__container`}
        flex
        padding={isMediumScreen ? [false] : [false, "lg"]}
        direction="row"
        align="center"
        justify="space-between">
        <Flex className={`${baseClass}__left`} align="center" justify="flex-start" innerMargin="md" grow={1}>
          {!isMediumScreen && (
            <Flex
              padding={[false, "xl", false, false]}
              direction="row"
              align="center"
              justify="flex-start"
              innerMargin="md">
              {isPublic ? (
                <Link to={`/${lang}${router.get("public-job-requests").path}`} target="_self">
                  <EworkLogo orientation="landscape" size="xxl" />
                </Link>
              ) : (
                <Link to={router.get("dashboard").path} className={`${baseClass}__logo`} target="_self">
                  <EworkLogo orientation="landscape" size="xxl" />
                </Link>
              )}
            </Flex>
          )}
          {showNavigation &&
            !hasRequiredLegalDocumentsToAccept &&
            (authenticated ? (
              <Navigation onOpen={handleOpen} onClose={handleClose} />
            ) : (
              <PublicNavigation onOpen={handleOpen} onClose={handleClose} />
            ))}
        </Flex>
        {isMediumScreen && (
          <Flex direction="column" className={`${baseClass}__center`} align="flex-end" justify="center">
            {isPublic ? (
              <Link to={`/${lang}${router.get("public-job-requests").path}`} target="_self">
                <VeramaLogo orientation="landscape" size="xxl" />
              </Link>
            ) : (
              <Link to={router.get("dashboard").path} target="_self">
                <VeramaLogo orientation="landscape" size="xxl" />
              </Link>
            )}
          </Flex>
        )}

        {!hasRequiredLegalDocumentsToAccept && (
          <Flex className={`${baseClass}__right`} align="center" justify="flex-end" innerMargin="md" grow={1}>
            {authenticated ? <Authenticated /> : <NavButtons />}
          </Flex>
        )}
      </Container>
    </div>
  );
});
